import React from "react";
import styled from "styled-components";
import {Container, Flex} from "@modbros/components/layout";
import Colors from "@modbros/design-tokens/colors";
import Spacings from "@modbros/design-tokens/spacings";
import {CheckIcon, CloseIcon, DownloadIcon} from "@modbros/icons";
import {Heading, Text} from "@modbros/components/typography";
import {Button} from "@modbros/components/form";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPatreon} from "@fortawesome/free-brands-svg-icons";

const TierCard = styled.div`
    background: ${Colors.Container.Default};
    border: 1px solid ${Colors.Border.Default};
    border-radius: ${Spacings.Size12};
    padding: ${Spacings.Size20};
    width: 200px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${Spacings.Size20};

    :hover {
        box-shadow: 0 2px 8px 0 ${Colors.Container.InputHover};
    }
`;

const Price = styled.div`
    font-size: 20px;
    font-weight: bold;
    color: ${Colors.Text.Emphasized};
`;
const Features = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    width: 100%;
`;

const Feature = styled.li`
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 14px;
    gap: ${Spacings.Size8};
    color: ${Colors.Text.Default};
    margin-top: ${Spacings.Size4};
`;


const TierDescription = styled.div`
    min-height: 90px;
    text-align: center;
`;

const Amount = styled.div`
    width: 3rem;
    font-weight: bold;
    color: ${Colors.Text.Emphasized};
    display: flex;
    align-items: center;
    justify-content: right;
`;


const Kind = styled.div`
    flex: 1;
    text-align: left;
    color: ${Colors.Text.Default};
    font-weight: normal;
`;

type FeatureType = {
    amount: string | boolean | undefined;
    kind: string;
};

const PatreonButton = () =>
    <Button
        prefixIcon={
            <FontAwesomeIcon
                style={{width: "20px", height: "20px", paddingTop: "1px"}}
                icon={faPatreon}
            />
        }
        as={"a"}
        target="_blank"
        rel="noreferrer"
        href={`https://www.patreon.com/user?u=34439889`}
        className="link-unstyled"
        style={{
            background: "#f96854",
        }}
    >
        Patreon
    </Button>;

const tiers = [
    {
        name: "Free",
        price: <>&nbsp;</>,
        desc: "Start using MoBro for free! Perfect for basic PC monitoring and already covering most dashboarding needs.",
        features: [
            {amount: "1", kind: "Dashboard"},
            {amount: "2 sec", kind: "Dashboard Update frequency"},
            {amount: "25", kind: "Metrics per dashboard"}
        ],
        button: (
            <Button
                className="link-unstyled"
                as={"a"}
                href="https://update.v1.mobro.app/download/latest"
                target="_blank"
                prefixIcon={<DownloadIcon/>}
                variant="primary"
            >
                Install
            </Button>
        ),
    },
    {
        name: "Bro",
        price: "3€",
        desc: "Upgrade your dashboarding experience! Unlock more dashboards, enjoy faster metric updates and track additional metrics.",
        features: [
            {amount: "4", kind: "Dashboards"},
            {amount: "1 sec", kind: "Dashboard Update frequency"},
            {amount: "75", kind: "Metrics per dashboard"},
            {amount: true, kind: "Access to premium widget packs/plugins"},
            {amount: true, kind: "Tier specific Badge"},
            {amount: true, kind: "Access to Patreon Discord Channel"},
        ],
        button: (
            <PatreonButton/>
        ),
    },
    {
        name: "Super Bro",
        price: "7€",
        desc: "Take your dashboards to the next level! Track twice as many metrics and enjoy even faster updates. Start creating interactive dashboards by adding actions and gain access to metric data exports.",
        features: [
            {amount: "4", kind: "Dashboards"},
            {amount: "0.5 sec", kind: "Dashboard Update frequency"},
            {amount: "150", kind: "Metrics per dashboard"},
            {amount: true, kind: "Access to premium widget packs/plugins"},
            {amount: true, kind: "Tier specific Badge"},
            {amount: true, kind: "Access to Patreon Discord Channel"},
            {amount: true, kind: "Metric Export"},
        ],
        button: (
            <PatreonButton/>
        ),
    },
    {
        name: "Mega Bro",
        price: "11€",
        desc: "Unlock the full MoBro experience! Get unlimited metrics and actions for complete flexibility. Plus, access a dedicated Discord support channel for personal support.",
        features: [
            {amount: "10", kind: "Dashboards"},
            {amount: "0.5", kind: "Dashboard Update frequency (sec)"},
            {amount: "Unl.", kind: "Metrics per dashboard"},
            {amount: true, kind: "Access to premium widget packs/plugins"},
            {amount: true, kind: "Tier specific Badge"},
            {amount: true, kind: "Access to Patreon Discord Channel"},
            {amount: true, kind: "Metric Export"},
            {amount: true, kind: "Personal Support"},
        ],
        button: (
            <PatreonButton/>
        ),
    },
];

export const PriceTable = () => {
    return (
        <Container paddingY={12} marginY={12}>
            <Flex
                flexDirection="row"
                justifyContent="center"
                gap={12}
                flexWrap="wrap"
            >
                {tiers.map((tier, index) => (
                    <TierCard key={index}>
                        <Heading level={3}>
                            {tier.name}
                        </Heading>
                        <Text textStyle={"default-emphasized"}>{tier.price}</Text>
                        <TierDescription>
                            <Text textStyle={"small"}>{tier.desc}</Text>
                        </TierDescription>
                        <Features>
                            {tier.features.map((feature: FeatureType, featureIndex: number) => (
                                <Feature key={featureIndex}>
                                    <Amount>
                                        {typeof feature.amount === "string"
                                            ? feature.amount
                                            : feature.amount === true
                                                ? <CheckIcon/>
                                                : feature.amount === false
                                                    ? <CloseIcon/>
                                                    : ""}
                                    </Amount>
                                    <Kind>
                                        {feature.kind}
                                    </Kind>
                                </Feature>
                            ))}

                        </Features>
                        <Flex justifyContent="center" style={{marginTop: 'auto'}}>{tier.button}</Flex>
                    </TierCard>
                ))}
            </Flex>
        </Container>
    );
};
